import { IApiResponse } from '@/common/models/interface'
import api from '@/modules/api'

import { AxiosResponse } from 'axios'
import { VisitorAgeStatisticsRsp } from './models/GupBridge/VisitorAgeStatisticsRsp'
import { TrendRsp } from './models/GupBridge/TrendRsp'
import { EnumTokenType, EnumTxType } from '@/common/models/enum'
import { VisitorTrendRsp } from './models/GupBridge/VisitorTrendRsp'
import { PointStatisticsRsp } from './models/GupBridge/PointStatisticsRsp'
import { CourseStatisticsRsp } from './models/GupBridge/CourseStatisticsRsp'
import { PointTxHistoryRsp } from './models/GupBridge/PointTxHistoryRsp'
import { CourseTxHistoryRsp } from './models/GupBridge/CourseTxHistoryRsp'
import { VisitorMetricsRsp } from './models/GupBridge/VisitorMetricsRsp'
import GupBridgeTokenRsp from './models/GupBridge/GupBridgeTokenRsp'

export default class GupBridgeService {
  public async getStatisticsPoint(tokenAddress?: any): Promise<IApiResponse<any[]>> {
    const res: AxiosResponse = await api.get(
      `gupbridge/CustomerStatistics/point${tokenAddress ? '?tokenAddr=' + tokenAddress : ''}`
    )
    return res.data
  }
  public async getStatisticsCourse(tokenAddress?: any): Promise<IApiResponse<any[]>> {
    const res: AxiosResponse = await api.get(
      `gupbridge/CustomerStatistics/course${tokenAddress ? '?tokenAddr=' + tokenAddress : ''}`
    )
    return res.data
  }
  public async getCustomer(param?: any): Promise<IApiResponse<any>> {
    const res: AxiosResponse = await api.post('gupbridge/customers', param)
    return { ...res.data, headers: res.headers }
  }
  public async postCustomerExport(param?: any): Promise<AxiosResponse<any>> {
    const res: AxiosResponse = await api.post('gupbridge/ExportCustomer', param)
    return res
  }
  public async postExportTransaction(param?: any): Promise<AxiosResponse<any>> {
    const res: AxiosResponse = await api.post('gupbridge/ExportTransaction', param)
    return res
  }
  public async getVisitorMetrics(): Promise<IApiResponse<VisitorMetricsRsp>> {
    const res: AxiosResponse = await api.get('gupbridge/VisitorMetrics')
    return res.data
  }
  public async getTokens(param?: EnumTokenType): Promise<IApiResponse<GupBridgeTokenRsp[]>> {
    const res: AxiosResponse = await api.get(`gupbridge/Tokens/${param}`)
    return res.data
  }
  public async getVisitorAgeStatistics(param: {
    txType?: EnumTxType
    tokenType?: EnumTokenType
    startDate: string
    endDate: string
    tokenAddresses?: string[]
    contractAddresses?: string[]
    triggers?: string[],
    instructor?: string
  }): Promise<IApiResponse<VisitorAgeStatisticsRsp[]>> {
    const { txType, tokenType, startDate, endDate, tokenAddresses, contractAddresses, triggers, instructor } =
      param
    const requestParam: any = {
      startDate,
      endDate
    }
    if (contractAddresses?.length > 0) {
      requestParam.contractAddresses = contractAddresses
    }
    if (tokenAddresses?.length > 0) {
      requestParam.tokenAddresses = tokenAddresses
    }
    if (tokenType) {
      requestParam.tokenType = tokenType
    }
    if (txType) {
      requestParam.txType = txType
    }

    if (triggers?.length > 0) {
      requestParam.triggers = triggers
    }
    if (instructor != null && instructor != '') {
      requestParam.instructor = instructor
    }
    let res: AxiosResponse = await api.post('gupbridge/VisitorAgeStatistics', requestParam)
    return res.data
  }
  public async getVisitorTrends(param: {
    tokenType: EnumTokenType
    startDate: string
    endDate: string
    txType?: EnumTxType
    tokenAddresses?: string[]
    contractAddresses?: string[]
    triggers?: string[],
    instructor?: string
  }): Promise<IApiResponse<VisitorTrendRsp[]>> {
    const { tokenType, startDate, endDate, txType, tokenAddresses, contractAddresses, triggers, instructor } =
      param
    const requestParam: any = {
      startDate,
      endDate
    }
    if (contractAddresses?.length > 0) {
      requestParam.contractAddresses = contractAddresses
    }
    if (tokenAddresses?.length > 0) {
      requestParam.tokenAddresses = tokenAddresses
    }
    if (tokenType) {
      requestParam.tokenType = tokenType
    }
    if (txType) {
      requestParam.txType = txType
    }

    if (triggers?.length > 0) {
      requestParam.triggers = triggers
    }
    if (instructor != null && instructor != '') {
      requestParam.instructor = instructor
    }
    let res: AxiosResponse = await api.post('gupbridge/VisitorTrends', requestParam)
    return res.data
  }
  public async getPointTrends(param: {
    txType: EnumTxType
    startDate: string
    endDate: string
    tokenAddresses?: string[]
    contractAddresses?: string[]
    triggers?: string[]
  }): Promise<IApiResponse<TrendRsp[]>> {
    const { txType, startDate, endDate, tokenAddresses, contractAddresses, triggers } = param
    const requestParam: any = {
      startDate,
      endDate
    }
    if (tokenAddresses?.length > 0) {
      requestParam.tokenAddresses = tokenAddresses
    }
    if (contractAddresses?.length > 0) {
      requestParam.contractAddresses = contractAddresses
    }
    if (txType) {
      requestParam.txType = txType
    }
    if (triggers?.length > 0) {
      requestParam.triggers = triggers
    }
    let res: AxiosResponse = await api.post('gupbridge/PointTrends', requestParam)
    return res.data
  }
  public async getCourseTrends(param: {
    txType: EnumTxType
    startDate: string
    endDate: string
    tokenAddresses?: string[],
    instructor?: string
    triggers?: string[]
  }): Promise<IApiResponse<TrendRsp[]>> {
    const { txType, startDate, endDate, tokenAddresses, instructor, triggers } = param
    const requestParam: any = {
      txType,
      startDate,
      endDate
    }
    if (tokenAddresses?.length > 0) {
      requestParam.tokenAddresses = tokenAddresses
    }
    if (instructor != null && instructor != '') {
      requestParam.instructor = instructor
    }
    if (triggers?.length > 0) {
      requestParam.triggers = triggers
    }
    let res: AxiosResponse = await api.post('gupbridge/CourseTrends', requestParam)
    return res.data
  }
  public async getPointStatistics(param: {
    txType: EnumTxType
    startDate: string
    endDate: string
    tokenAddresses?: string[]
    contractAddresses?: string[]
    triggers?: string[]
  }): Promise<IApiResponse<PointStatisticsRsp[]>> {
    const { txType, startDate, endDate, tokenAddresses, contractAddresses, triggers } = param
    const requestParam: any = {
      startDate,
      endDate
    }
    if (contractAddresses?.length > 0) {
      requestParam.contractAddresses = contractAddresses
    }
    if (tokenAddresses?.length > 0) {
      requestParam.tokenAddresses = tokenAddresses
    }
    if (txType) {
      requestParam.txType = txType
    }
    if (triggers?.length > 0) {
      requestParam.triggers = triggers
    }
    let res: AxiosResponse = await api.post('gupbridge/PointStatistics', requestParam)
    return res.data
  }
  public async getCourseStatistics(param: {
    startDate: string
    endDate: string
    tokenAddresses?: string[],
    instructor?: string
  }): Promise<IApiResponse<CourseStatisticsRsp[]>> {
    const { startDate, endDate, tokenAddresses, instructor } = param
    const requestParam: any = {
      startDate,
      endDate
    }
    if (tokenAddresses?.length > 0) {
      requestParam.tokenAddresses = tokenAddresses
    }
    if (instructor != null && instructor != '') {
      requestParam.instructor = instructor
    }
    let res: AxiosResponse = await api.post('gupbridge/CourseStatistics', requestParam)
    return res.data
  }
  public async getPointTxHistory(param: any): Promise<IApiResponse<PointTxHistoryRsp[]>> {
    const res = await api.post('gupbridge/PointTxHistory', param)
    return { ...res.data, headers: res.headers }
  }
  public async getCourseTxHistory(param: any): Promise<IApiResponse<CourseTxHistoryRsp[]>> {
    const res = await api.post('gupbridge/CourseTxHistory', param)
    return { ...res.data, headers: res.headers }
  }
}
